import * as React from 'react';
import { Picker, Item, Flex, Text } from '@adobe/react-spectrum';
import { IFilterControl, IFilterItem } from '../../FilterSetupPlaceholder';
import { OperatorSelector, IOperator } from '../OperatorSelector';
import { useFilterGuard } from '../../../../../../../hooks/useFilterGuard';
import styles from './BooleanFilterControl.module.css';

export interface IBooleanFilterControlProps {
  filter: IFilterControl;
  onFilterChange: (filter: IFilterControl) => void;
}

const BOOLEAN_OPERATORS: IOperator[] = [
  { key: 'is_true', label: 'Is True' },
  { key: 'is_false', label: 'Is False' },
  { key: 'is_empty', label: 'Is Empty' },
  { key: 'is_not_empty', label: 'Is Not Empty' },
];

const BOOLEAN_VALUES = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
  { id: 'empty', label: 'Empty' },
];

export const BooleanFilterControl = React.forwardRef<HTMLDivElement, IBooleanFilterControlProps>(
  (props, forwardedRef) => {
    const { filter, onFilterChange } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    const currentFilterItem = filter.filterInfo[0] || {
      name: 'value',
      type: 'is_true',
      value: '',
    };

    const [operator, setOperator] = React.useState<string>(currentFilterItem.type || 'is_true');
    const [value, setValue] = React.useState<string>(currentFilterItem.value || 'yes');

    React.useEffect(() => {
      syncGuard(filter);
    }, [filter, syncGuard]);

    React.useEffect(() => {
      const currentItem = filter.filterInfo[0];
      if (currentItem) {
        setOperator(currentItem.type || 'is_true');
        setValue(currentItem.value || 'yes');
      }
    }, [filter.filterInfo]);

    const handleOperatorChange = React.useCallback(
      (newOperator: string) => {
        setOperator(newOperator);
        // Map operator to value
        let mappedValue = 'yes';
        if (newOperator === 'is_false') {
          mappedValue = 'no';
        } else if (newOperator === 'is_empty') {
          mappedValue = 'empty';
        } else if (newOperator === 'is_not_empty') {
          mappedValue = 'yes';
        }

        const updatedFilterInfo: IFilterItem[] = [
          {
            ...currentFilterItem,
            type: newOperator,
            value: mappedValue,
          },
        ];
        notifyChange({
          ...filter,
          filterInfo: updatedFilterInfo,
        });
      },
      [filter, currentFilterItem, notifyChange]
    );

    const handleValueChange = React.useCallback(
      (newValue: string) => {
        setValue(newValue);
        const updatedFilterInfo: IFilterItem[] = [
          {
            ...currentFilterItem,
            type: operator,
            value: newValue,
          },
        ];
        notifyChange({
          ...filter,
          filterInfo: updatedFilterInfo,
        });
      },
      [filter, currentFilterItem, operator, notifyChange]
    );

    // For is_empty and is_not_empty, hide the value picker
    const showValuePicker = operator !== 'is_empty' && operator !== 'is_not_empty';

    return (
      <div ref={forwardedRef} className={styles.booleanFilterControl}>
        <Flex direction="column" gap="size-200">
          {/* Operator row: Label + Selector */}
          <Flex 
            direction="row" 
            alignItems="center" 
            justifyContent="space-between"
            gap="size-200"
            UNSAFE_className={styles.operatorRow}
          >
            <Text UNSAFE_className={styles.operatorLabel}>{filter.label || filter.name}</Text>
            <div className={styles.operatorSelectorWrapper}>
              <OperatorSelector
                operators={BOOLEAN_OPERATORS}
                selectedOperator={operator}
                onOperatorChange={handleOperatorChange}
                aria-label="Boolean filter operator"
              />
            </div>
          </Flex>
          
          {/* Value input row */}
          {showValuePicker && (
            <div className={styles.valueInput}>
              <Picker
                selectedKey={value}
                onSelectionChange={(key) => handleValueChange(key as string)}
                width="100%"
                aria-label="Boolean filter value"
              >
                {BOOLEAN_VALUES.map((val) => (
                  <Item key={val.id}>{val.label}</Item>
                ))}
              </Picker>
            </div>
          )}
        </Flex>
      </div>
    );
  }
);

BooleanFilterControl.displayName = 'BooleanFilterControl';

